import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useUpdateTimeOff } from "@/features/calendar/hooks/use-timeoffs";
import type { TimeOff } from "@/features/calendar/types";
import { useEffect, useState } from "react";
import { useTranslation } from "@/features/utils/i18n";
import { toast } from "sonner";
import { TimeOffForm } from "./timeoff-form";

interface EditTimeOffDialogProps {
  timeoff: TimeOff | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EditTimeOffDialog = ({
  timeoff,
  open,
  onOpenChange,
}: EditTimeOffDialogProps) => {
  const { t } = useTranslation("schedules");
  const [reason, setReason] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [affectedMemberIds, setAffectedMemberIds] = useState<string[]>([]);

  const updateTimeOff = useUpdateTimeOff();

  useEffect(() => {
    if (timeoff) {
      setReason(timeoff.reason);
      setStartDate(timeoff.start_date.split("T")[0]); // Extract date part
      setEndDate(timeoff.end_date.split("T")[0]); // Extract date part
      setAffectedMemberIds(timeoff.affected_members?.id || []);
    }
  }, [timeoff]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!timeoff) return;

    try {
      await updateTimeOff.mutateAsync({
        timeoffId: timeoff.id,
        data: {
          reason,
          start_date: startDate,
          end_date: endDate,
          affected_members: { id: affectedMemberIds },
        },
      });

      toast.success(t("closures.messages.updated"));
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("closures.messages.updateError"),
      );
    }
  };

  if (!timeoff) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("closures.editDialog.title")}</DialogTitle>
          <DialogDescription>
            {t("closures.editDialog.description")}
          </DialogDescription>
        </DialogHeader>

        <TimeOffForm
          reason={reason}
          startDate={startDate}
          endDate={endDate}
          affectedMemberIds={affectedMemberIds}
          onReasonChange={setReason}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onAffectedMembersChange={setAffectedMemberIds}
          onSubmit={handleSubmit}
          onCancel={() => onOpenChange(false)}
          submitLabel={t("closures.form.save")}
          isSubmitting={updateTimeOff.isPending}
        />
      </DialogContent>
    </Dialog>
  );
};
